import type { KeystrokeLog } from './types'

/** Logs longer than this are compressed so the replay lands on REPLAY_TARGET_MS. */
export const REPLAY_MAX_MS = 90_000
export const REPLAY_TARGET_MS = 75_000

export interface ReplaySchedule {
  /** Absolute replay time in ms at which each op fires. */
  readonly times: readonly number[]
  readonly totalMs: number
  /** Sum of the (capped) dt values. */
  readonly realMs: number
  /** Real ms per replay ms. */
  readonly factor: number
}

export interface ScheduleOptions {
  /** 1 = real time, 2 = twice as fast. Omit for the default 60-90 s replay. */
  readonly speed?: number
}

function defaultFactor(realMs: number): number {
  return realMs <= REPLAY_MAX_MS ? 1 : realMs / REPLAY_TARGET_MS
}

export function scheduleReplay(log: KeystrokeLog, options: ScheduleOptions = {}): ReplaySchedule {
  if (options.speed !== undefined && !(options.speed > 0)) {
    throw new RangeError(`replay speed must be positive, got ${options.speed}`)
  }
  const realMs = log.ops.reduce((sum, op) => sum + op.dt, 0)
  const factor = options.speed ?? defaultFactor(realMs)
  let elapsed = 0
  const times = log.ops.map((op) => {
    elapsed += op.dt
    return elapsed / factor
  })
  return { times, totalMs: realMs / factor, realMs, factor }
}

/** How many ops have fired at replay time `tMs` (ops with time <= tMs). */
export function opIndexAt(schedule: ReplaySchedule, tMs: number): number {
  const { times } = schedule
  let lo = 0
  let hi = times.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if ((times[mid] ?? Infinity) <= tMs) lo = mid + 1
    else hi = mid
  }
  return lo
}

export function thinkingLabel(pauseSeconds: number): string {
  const total = Math.max(0, Math.round(pauseSeconds))
  if (total === 0) return '…thinking'
  if (total < 60) return `…thinking ${total}s`
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (hours === 0) return `…thinking ${minutes}m`
  return minutes === 0 ? `…thinking ${hours}h` : `…thinking ${hours}h ${minutes}m`
}
